import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Product } from '../../services/api';
import type { ErpResyncOptions } from '../../services/api';
import { NormalizedProductVariant } from '../../utils/productVariants';
import { CameraWithPricing, ExpandableDescription, OverrideFormState, ProductPriceHistoryRow } from '../../utils/productDetailHelpers';
import { stripHtml } from '../../utils/text';
import { ErpSyncAuditPanel } from '../ErpSyncAuditPanel';
import { ComboRebateOffersNote } from '../ComboRebateOffersNote';
import { ProductGalleryAndIdentifiers } from './ProductGalleryAndIdentifiers';
import { ProductPriceBlock } from './ProductPriceBlock';
import { StoreLocationPricePanel } from './StoreLocationPricePanel';
import { TradeInCreditLine } from './TradeInCreditLine';
import { ProductActionsRow } from './ProductActionsRow';
import { ProductVariantsTable } from './ProductVariantsTable';
import { ProductMarketPricing } from './ProductMarketPricing';
import { ProductOverridesAiCopy, ProductOverridesAiSpecs, ProductOverridesSection } from './ProductOverridesSection';
import type { StoreSpecsEditorProps } from './StoreSpecsEditor';
import { NeutralNote } from './NeutralNote';
import { ProductWorksWith } from './ProductWorksWith';
import { StoreRelatedProductsEditor } from './StoreRelatedProductsEditor';
import { StorePreorderGoLiveEditor } from './StorePreorderGoLiveEditor';
import type { StorePreorderGoLiveState } from './useStorePreorderGoLive';
import type { StoreRelatedPicksState } from './useStoreRelatedPicks';
import { FieldSourceBadge } from './FieldSourceBadge';
import { tokens } from '../../theme/tokens';

const DESCRIPTION_PREVIEW_LENGTH = 350;

type FieldSource = NonNullable<Product['fieldSources']>[keyof NonNullable<Product['fieldSources']>];
const sourcesDiffer = (a?: FieldSource, b?: FieldSource) => a?.source !== b?.source || a?.confidence !== b?.confidence;

export interface ProductOverviewTabProps {
  product: CameraWithPricing;
  displayVariants: NormalizedProductVariant[];
  linkedVariants: Product[];
  linkedVariantsLoading: boolean;
  variantSwitchLoadingSku: string | null;
  onVariantClick: (variant: NormalizedProductVariant) => void;
  onLinkedProductClick: (product: Product) => void;
  onOpenCompatibleProduct: (item: import('./ProductWorksWith').CompatibilityItem) => void;
  productPriceHistory: ProductPriceHistoryRow[];

  isStoreScoped: boolean;
  isVendor: boolean;
  isPlatformAdmin: boolean;
  inSync: boolean;

  onAddToSync: () => void;
  addToSyncPending: boolean;
  onForceResync: (opts?: ErpResyncOptions) => void;
  forceResyncPending: boolean;
  onRemoveFromSync: () => void;
  removeFromSyncPending: boolean;
  onViewSyncSelection: () => void;
  onOpenGallery: (images: string[], title: string, index: number) => void;

  onReviewDiscount: (decision: 'approved' | 'rejected') => void;
  reviewPending: boolean;

  onRefreshMarketPricing: () => void;
  refreshMarketPricingPending: boolean;

  showStoreOverridesPanel: boolean;
  overridesExpanded: boolean;
  onToggleOverridesExpanded: () => void;
  overrideForm: OverrideFormState;
  onOverrideFormChange: (updater: (prev: OverrideFormState) => OverrideFormState) => void;
  canManageStoreOverrides: boolean;
  overrideLoading: boolean;
  onSaveOverrides: () => void;
  savePending: boolean;
  onClearOverrides: () => void;
  clearPending: boolean;
  onGenerateDescription: () => void;
  generatePending: boolean;
  allowBelowMap: boolean;
  /** Live price of the store's mapped Shopify listing, from the sync audit read. */
  storefrontPrice?: number | null;
  /** The store's own AI copy draft and actions (flag `store_ai_copy`); null while the flag is off. */
  aiCopy?: ProductOverridesAiCopy | null;
  /** Approved bulk-AI spec suggestions (flag `bulk_ai_operations`); null while the flag is off. */
  aiSpecs?: ProductOverridesAiSpecs | null;
  ownSpecs?: StoreSpecsEditorProps | null;
  /** The store's related and upsell picks (flag `retailer_related_products`); null while the flag is off. */
  relatedPicks?: StoreRelatedPicksState | null;
  /** The store's pre-order go-live (flag `store_preorder_go_live`, MOT-766); null while the flag is off. */
  preorderGoLive?: StorePreorderGoLiveState | null;
}

export function ProductOverviewTab(props: ProductOverviewTabProps) {
  const { product } = props;
  const fullText = product.description ? stripHtml(product.description) : '';
  const isLongDescription = fullText.length > DESCRIPTION_PREVIEW_LENGTH;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '400px 1fr' }, alignItems: 'start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: { xs: 2, md: '22px' }, borderRight: { md: `1px solid ${tokens.line.hairline}` }, borderBottom: { xs: `1px solid ${tokens.line.hairline}`, md: 'none' } }}>
        <ProductGalleryAndIdentifiers product={product} onOpenGallery={props.onOpenGallery} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.25, md: 3 }, p: { xs: 2, md: '22px' } }}>
        <ProductPriceBlock
          product={product}
          history={props.productPriceHistory}
          isPlatformAdmin={props.isPlatformAdmin}
          isVendor={props.isVendor}
          onReviewDiscount={props.onReviewDiscount}
          reviewPending={props.reviewPending}
          storefrontPrice={props.isStoreScoped ? props.storefrontPrice : null}
        />

        {props.isStoreScoped && (
          <StoreLocationPricePanel product={product} />
        )}

        {props.isStoreScoped && product.id && <TradeInCreditLine productId={product.id} />}

        {product.isProOnly && (
          <NeutralNote>
            This product is not available on the consumer site — sold exclusively through the manufacturer's professional channel.
            {product.proSiteUrl && (
              <Button
                href={product.proSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ml: 1, p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600, fontSize: 'inherit', color: 'primary.main' }}
              >
                Visit Pro Site
              </Button>
            )}
          </NeutralNote>
        )}

        <ComboRebateOffersNote offers={product.pricingData?.comboOffers} />

        <ProductActionsRow
          product={product}
          inSync={props.inSync}
          isStoreScoped={props.isStoreScoped}
          isVendor={props.isVendor}
          isPlatformAdmin={props.isPlatformAdmin}
          onAddToSync={props.onAddToSync}
          addToSyncPending={props.addToSyncPending}
          onForceResync={props.onForceResync}
          forceResyncPending={props.forceResyncPending}
          onRemoveFromSync={props.onRemoveFromSync}
          removeFromSyncPending={props.removeFromSyncPending}
          onViewSyncSelection={props.onViewSyncSelection}
        />

        <ProductVariantsTable
          product={product}
          variants={props.displayVariants}
          linkedVariants={props.linkedVariants}
          linkedVariantsLoading={props.linkedVariantsLoading}
          variantSwitchLoadingSku={props.variantSwitchLoadingSku}
          isStoreScoped={props.isStoreScoped}
          onVariantClick={props.onVariantClick}
          onLinkedProductClick={props.onLinkedProductClick}
        />

        <ProductWorksWith key={`works-with-${product.id}`} productId={product.id} compatibility={product.compatibility} onOpenProduct={props.onOpenCompatibleProduct} />

        {props.preorderGoLive && <StorePreorderGoLiveEditor state={props.preorderGoLive} />}

        {props.relatedPicks && (
          <StoreRelatedProductsEditor
            state={props.relatedPicks}
            onOpenProduct={(item) => props.onOpenCompatibleProduct({ id: item.id, name: item.name, brand: item.brand, image: item.image, mount: null, via: 'manual' })}
          />
        )}

        {(props.isStoreScoped || props.isPlatformAdmin) && (
          <ErpSyncAuditPanel key={product.id} productId={product.id} enabled={props.isStoreScoped} />
        )}

        <ProductMarketPricing
          product={product}
          onRefresh={props.onRefreshMarketPricing}
          refreshPending={props.refreshMarketPricingPending}
        />

        {(product.shortDescription || fullText) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.faint }}>
                Description
              </Typography>
              <FieldSourceBadge product={product} field={fullText ? 'description' : 'shortDescription'} />
            </Box>
            {product.shortDescription && (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, lineHeight: 1.55, color: 'text.primary' }}>
                {product.shortDescription}
                {fullText && sourcesDiffer(product.fieldSources?.shortDescription, product.fieldSources?.description) && (
                  <Box component="span" sx={{ ml: 0.75, verticalAlign: 'middle' }}>
                    <FieldSourceBadge product={product} field="shortDescription" />
                  </Box>
                )}
              </Typography>
            )}
            {fullText && (
              isLongDescription ? (
                <ExpandableDescription text={fullText} />
              ) : (
                <Typography sx={{ fontSize: '13.5px', lineHeight: 1.65, color: 'text.secondary' }}>{fullText}</Typography>
              )
            )}
          </Box>
        )}

        {props.showStoreOverridesPanel && (
          <ProductOverridesSection
            product={product}
            form={props.overrideForm}
            onFormChange={props.onOverrideFormChange}
            expanded={props.overridesExpanded}
            onToggleExpanded={props.onToggleOverridesExpanded}
            canManageStoreOverrides={props.canManageStoreOverrides}
            overrideLoading={props.overrideLoading}
            onSave={props.onSaveOverrides}
            savePending={props.savePending}
            onClear={props.onClearOverrides}
            clearPending={props.clearPending}
            onGenerateDescription={props.onGenerateDescription}
            generatePending={props.generatePending}
            allowBelowMap={props.allowBelowMap}
            aiCopy={props.aiCopy}
            aiSpecs={props.aiSpecs}
            ownSpecs={props.ownSpecs}
          />
        )}
      </Box>
    </Box>
  );
}
